import React, { FC, useMemo } from "react";
import {
  alpha,
  Box,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import type { Matchs, Table, Tournoi } from "../../../types";
import { buildAdminMatchsUrl } from "./occupancyNavigation";

/**
 * Durées estimées (minutes) pour « prochaines libérations » — P75 observé juin 2025.
 * Surchargeables via tournoi.estimatedMatchDurationMinutes.
 */
const DEFAULT_DURATION_MIN: Record<2 | 3, number> = {
  2: 40,
  3: 90,
};

export type NowBlock = {
  match: Matchs;
  start: Date;
  end: Date;
};

type TablesOccupancyNowProps = {
  tables: Table[];
  /** Matchs ENCOURS pour estimer l’occupation live. */
  liveBlocks: NowBlock[];
  now: Date;
  estimatedMatchDurationMinutes?: Tournoi["estimatedMatchDurationMinutes"];
};

function formatTime(date: Date): string {
  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function playerCount(match: Matchs): number {
  return match.matchsPlayerParticipations?.length ?? 0;
}

/** NOM + initiale du prénom (ex. « DUPONT T. ») — aligné Matchs (name=nom, surname=prénom). */
function playerCompactLabel(match: Matchs): string {
  const parts = (match.matchsPlayerParticipations ?? [])
    .slice()
    .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))
    .map((p) => {
      const player = p.players;
      if (!player) return null;
      const nom = (player.name ?? "").trim().toLocaleUpperCase("fr");
      const prenom = (player.surname ?? "").trim();
      if (!nom && !prenom) return null;
      if (!nom) return prenom;
      if (!prenom) return nom;
      return `${nom} ${prenom.charAt(0).toLocaleUpperCase("fr")}.`;
    })
    .filter((label): label is string => Boolean(label));

  return parts.length > 0 ? parts.join(" · ") : "Match en cours";
}

function playerLines(match: Matchs): string[] {
  const label = playerCompactLabel(match);
  if (label === "Match en cours") return [label];
  return label.split(" · ");
}

/**
 * Estimation de fin :
 * 1. si dateFin déjà posée → l’utiliser
 * 2. sinon dateLancement + durée selon nb joueurs (2 ou 3+)
 */
function estimatedDurationMinutes(
  match: Matchs,
  configured?: Tournoi["estimatedMatchDurationMinutes"]
): number {
  const n = playerCount(match) >= 3 ? 3 : 2;
  const fromTournoi = configured?.[String(n) as "2" | "3"];
  if (typeof fromTournoi === "number" && fromTournoi > 0) {
    return fromTournoi;
  }
  return DEFAULT_DURATION_MIN[n];
}

function computeEstimatedEnd(
  block: NowBlock,
  configured?: Tournoi["estimatedMatchDurationMinutes"]
): Date {
  if (block.match.dateFin instanceof Date) {
    return block.end;
  }
  const minutes = estimatedDurationMinutes(block.match, configured);
  return new Date(block.start.getTime() + minutes * 60_000);
}

const TablesOccupancyNow: FC<TablesOccupancyNowProps> = ({
  tables,
  liveBlocks,
  now,
  estimatedMatchDurationMinutes,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  const sortedTables = useMemo(
    () => [...tables].sort((a, b) => a.tableId - b.tableId),
    [tables]
  );

  const byTable = useMemo(() => {
    const map = new Map<number, NowBlock>();
    for (const block of liveBlocks) {
      if (block.match.tableId == null) continue;
      if (block.match.status !== "ENCOURS") continue;
      const existing = map.get(block.match.tableId);
      if (!existing || block.start.getTime() > existing.start.getTime()) {
        map.set(block.match.tableId, block);
      }
    }
    return map;
  }, [liveBlocks]);

  const rows = useMemo(() => {
    return sortedTables.map((table) => {
      const block = byTable.get(table.tableId);
      if (!block) {
        return {
          table,
          occupied: false as const,
        };
      }
      const estimatedEnd = computeEstimatedEnd(
        block,
        estimatedMatchDurationMinutes
      );
      const minutesLeft = Math.max(
        0,
        Math.round((estimatedEnd.getTime() - now.getTime()) / 60_000)
      );
      const nb = playerCount(block.match);
      return {
        table,
        occupied: true as const,
        block,
        estimatedEnd,
        minutesLeft,
        isOverdue: estimatedEnd.getTime() < now.getTime(),
        isThreePlayers: nb >= 3,
        playerLines: playerLines(block.match),
        playerTitle: playerCompactLabel(block.match),
      };
    });
  }, [sortedTables, byTable, now, estimatedMatchDurationMinutes]);

  const occupiedCount = rows.filter((r) => r.occupied).length;
  const freeCount = rows.length - occupiedCount;

  const nextFree = useMemo(() => {
    return rows
      .filter(
        (r): r is Extract<(typeof rows)[number], { occupied: true }> =>
          r.occupied
      )
      .slice()
      .sort((a, b) => a.estimatedEnd.getTime() - b.estimatedEnd.getTime())
      .slice(0, 5);
  }, [rows]);

  if (tables.length === 0) {
    return (
      <Typography color="text.secondary">Aucune table configurée.</Typography>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Chip
          size="small"
          color="success"
          label={`${freeCount} libre${freeCount > 1 ? "s" : ""}`}
        />
        <Chip
          size="small"
          color="primary"
          label={`${occupiedCount} occupée${occupiedCount > 1 ? "s" : ""}`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`MAJ ${formatTime(now)}`}
        />
      </Stack>

      {nextFree.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#0B1F33", 0.04),
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Prochaines libérations estimées
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Fin ≈ lancement +{" "}
            {estimatedMatchDurationMinutes?.["2"] ?? DEFAULT_DURATION_MIN[2]}{" "}
            min (2j) /{" "}
            {estimatedMatchDurationMinutes?.["3"] ?? DEFAULT_DURATION_MIN[3]}{" "}
            min (3j) — P75 observé
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {nextFree.map((row) => (
              <Chip
                key={row.table.tableId}
                size="small"
                variant="outlined"
                color={row.isOverdue ? "warning" : "default"}
                label={`T${row.table.tableId}${
                  row.isThreePlayers ? " · 3j" : ""
                } → ${formatTime(row.estimatedEnd)}${
                  row.isOverdue
                    ? " (dépassé)"
                    : ` (~${row.minutesLeft} min)`
                }`}
                onClick={() =>
                  navigate(
                    buildAdminMatchsUrl({
                      matchId: row.block.match.id
                        ? String(row.block.match.id)
                        : undefined,
                      tableId: row.table.tableId,
                      status: "encours",
                    })
                  )
                }
              />
            ))}
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 1.25,
        }}
      >
        {rows.map((row) => {
          const free = !row.occupied;
          return (
            <Box
              key={row.table.tableId}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (row.occupied) {
                  navigate(
                    buildAdminMatchsUrl({
                      matchId: row.block.match.id
                        ? String(row.block.match.id)
                        : undefined,
                      tableId: row.table.tableId,
                      status: "encours",
                    })
                  );
                } else {
                  navigate(
                    buildAdminMatchsUrl({
                      tableId: row.table.tableId,
                    })
                  );
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  (e.currentTarget as HTMLElement).click();
                }
              }}
              sx={{
                p: 1.5,
                borderRadius: 2,
                cursor: "pointer",
                border: 1,
                borderColor: free
                  ? alpha(theme.palette.success.main, 0.45)
                  : row.isThreePlayers
                    ? alpha("#C9920A", 0.55)
                    : alpha(theme.palette.primary.main, 0.45),
                bgcolor: free
                  ? alpha(theme.palette.success.main, isDark ? 0.12 : 0.08)
                  : alpha(theme.palette.primary.main, isDark ? 0.14 : 0.1),
                transition: "transform 0.12s ease, box-shadow 0.12s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 4px 14px ${alpha("#000", 0.2)}`,
                },
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={0.5}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Table {row.table.tableId}
                </Typography>
                {row.occupied && row.isThreePlayers && (
                  <Chip
                    size="small"
                    label="3 joueurs"
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: alpha("#C9920A", isDark ? 0.25 : 0.18),
                      color: isDark ? "#F0C14A" : "#8A6A08",
                    }}
                  />
                )}
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: free ? "success.main" : "primary.main",
                }}
              >
                {free ? "Libre" : "Occupée"}
              </Typography>
              {row.occupied && (
                <>
                  <Box sx={{ mt: 0.75 }} title={row.playerTitle}>
                    {row.playerLines.map((line, lineIdx) => (
                      <Typography
                        key={`${row.table.tableId}-${lineIdx}-${line}`}
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: 12.5,
                          lineHeight: 1.25,
                        }}
                        noWrap
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Depuis {formatTime(row.block.start)}
                    {" · "}
                    {row.isOverdue
                      ? "durée dépassée"
                      : `fin ~${formatTime(row.estimatedEnd)}`}
                  </Typography>
                </>
              )}
              {row.table.roomName && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {row.table.roomName}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default TablesOccupancyNow;
